import React, { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { rtlEnable } from 'domain/env';
import {
  nextPaymentAction,
  paymentNavigationSelector,
  paymentPaymentIdSelector,
  clearPaymentAction,
} from 'domain/payments';

import IconButton from '@mui/material/IconButton';
import { Navigation, NavigationPagination } from './StyledComponents';

import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';

import elements from 'components/elements';

// 37 - left arrow
// 39 - right arrow
const keyCodes = [37, 39];

const mapStateToProps = (state: unknown) => ({
  navigation: paymentNavigationSelector(state),
  isRtl: rtlEnable(state),
  paymentId: paymentPaymentIdSelector(state),
});

const PaymentNavigation: React.FC = () => {
  const dispatch = useDispatch();
  const { navigation, isRtl, paymentId } = useSelector(mapStateToProps);
  const [isTransitionComplete, setTransitionComplete] = useState(false);

  const { ordinalNumberInList, totalNumberOfPayments, isSetRequest } = useMemo(
    () => ({
      ordinalNumberInList: navigation.get('ordinalNumberInList'),
      totalNumberOfPayments: navigation.get('totalNumberOfPayments'),
      isSetRequest: navigation.get('isSetRequest'),
    }),
    [navigation],
  );

  const isNextDisabled = useMemo(() => !navigation.get('listLength') || navigation.get('listLength') < 2, [navigation]);
  const isPrevDisabled = useMemo(
    () => !navigation.get('fp') || paymentId === navigation.get('fp'),
    [navigation, paymentId],
  );

  const ordinalNumber = useMemo(() => ordinalNumberInList || 1, [ordinalNumberInList]);

  const total = useMemo(
    () => (totalNumberOfPayments === 0 ? '-' : totalNumberOfPayments || 1),
    [totalNumberOfPayments],
  );

  const toNext = useCallback((shift: number) => dispatch(nextPaymentAction(shift)), [dispatch]);
  const handleToNext = useCallback(
    (shift: number) => {
      if (!isTransitionComplete) {
        setTransitionComplete(true);
        toNext(shift);
      }
    },
    [toNext, isTransitionComplete],
  );

  const getDirection = useCallback(
    (defaultDirection: string) => {
      if (isRtl && defaultDirection === 'prev') return 'next';
      if (isRtl && defaultDirection === 'next') return 'prev';

      return defaultDirection;
    },
    [isRtl],
  );

  const handleNext = useCallback(() => {
    if (isNextDisabled) return;
    handleToNext(1);
  }, [isNextDisabled, handleToNext]);

  const handlePrevious = useCallback(() => {
    if (isPrevDisabled) return;
    handleToNext(-1);
  }, [isPrevDisabled, handleToNext]);

  const handleNavigation = useCallback(
    (defaultDirection: string) => {
      const direction = getDirection(defaultDirection);

      return direction === 'prev' ? handlePrevious : handleNext;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPrevDisabled, isNextDisabled, getDirection, handlePrevious, handleNext],
  );

  const onClickPrev = useCallback(() => {
    handleNavigation('prev')();
  }, [handleNavigation]);

  const onClickNext = useCallback(() => {
    handleNavigation('next')();
  }, [handleNavigation]);

  const handleDisabled = useCallback(
    (defaultDirection: string) => {
      const direction = getDirection(defaultDirection);

      return direction === 'prev' ? isPrevDisabled : isNextDisabled;
    },
    [getDirection, isPrevDisabled, isNextDisabled],
  );

  const handleKeyDownNavigate = useCallback(
    (keyCode: number) => {
      switch (keyCode) {
        case 37: // 37 - left arrow
          handleNavigation('prev')();
          break;
        case 39: // 39 - right arrow
          handleNavigation('next')();
          break;
        default:
          break;
      }
    },
    [handleNavigation],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const { keyCode, ctrlKey, metaKey } = e;
      const isCaught = (ctrlKey || metaKey) && keyCodes.includes(keyCode);

      if (isCaught) {
        e.stopPropagation();
        e.preventDefault();
        if (total < 2) return false;
        handleKeyDownNavigate(keyCode);
      }

      return false;
    },
    [total, handleKeyDownNavigate],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch, handleKeyDown, navigation]);

  useEffect(() => {
    if (isTransitionComplete) {
      setTransitionComplete(false);
    }
  }, [paymentId]);

  useEffect(() => {
    return () => {
      dispatch(clearPaymentAction());
    };
  }, [dispatch]);

  return (
    !isSetRequest && (
      <Navigation direction="row" dir="ltr" data-element={elements.content.documents.navigation.container}>
        <IconButton
          color="primary"
          onClick={onClickPrev}
          disabled={handleDisabled('prev')}
          data-element={elements.content.documents.navigation.prev}
        >
          <ChevronLeftOutlinedIcon />
        </IconButton>
        <NavigationPagination dir="ltr">
          <span>{ordinalNumber}</span>
          <span>/</span>
          <span>{total}</span>
        </NavigationPagination>
        <IconButton
          color="primary"
          onClick={onClickNext}
          disabled={handleDisabled('next')}
          data-element={elements.content.documents.navigation.next}
        >
          <ChevronRightOutlinedIcon />
        </IconButton>
      </Navigation>
    )
  );
};

export default memo(PaymentNavigation);
